import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AdminSales from "../components/AdminSales";
import { Badge } from "../components/ui/badge";
import { Loader2, LogOut, DollarSign, TrendingUp, Users, Zap, Bot, Activity, Smartphone } from "lucide-react";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();
const PLAN_LABEL = { get_found: "Get Found", agentic_ready: "Agentic Ready", autonomous_commerce: "Autonomous Commerce" };

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [dash, setDash] = useState(null);
  const [agents, setAgents] = useState(null);
  const [report, setReport] = useState(null);
  const [outreach, setOutreach] = useState({ messages: [] });
  const [leads, setLeads] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [settings, setSettings] = useState({ founder_whatsapp: "" });
  const [disc, setDisc] = useState({ industry: "", count: 3 });
  const [replyText, setReplyText] = useState("");
  const [classifyRes, setClassifyRes] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "founder_admin") { navigate("/dashboard"); return; }
    Promise.all([
      api.get("/admin/dashboard").then((r) => setDash(r.data)),
      api.get("/admin/agents").then((r) => setAgents(r.data)),
      api.get("/admin/daily-report").then((r) => setReport(r.data)),
      api.get("/admin/outreach").then((r) => setOutreach(r.data)),
      api.get("/admin/leads").then((r) => setLeads(r.data.leads || [])),
      api.get("/admin/alerts").then((r) => setAlerts(r.data)),
      api.get("/admin/settings").then((r) => setSettings(r.data)),
    ]).catch(() => {});
  }, [user, loading, navigate]);

  const loadOutreach = () => api.get("/admin/outreach").then((r) => setOutreach(r.data));
  const loadLeads = () => api.get("/admin/leads").then((r) => setLeads(r.data.leads || []));
  const discover = async () => {
    if (!disc.industry) return toast.error("Enter an industry");
    toast.info("Discovering prospects…");
    try { const { data } = await api.post("/admin/outreach/discover", disc); toast.success(`Found ${data.created} prospects`); loadLeads(); }
    catch { toast.error("Discovery failed"); }
  };
  const genSeq = async (id) => {
    toast.info("Scanning + writing a 5-touch sequence…");
    try { await api.post(`/admin/outreach/generate/${id}`); toast.success("Sequence queued"); loadOutreach(); loadLeads(); }
    catch { toast.error("Generation failed"); }
  };
  const testSend = async (id) => {
    try { const { data } = await api.post(`/admin/outreach/${id}/test-send`); toast.success(data.sent ? `Preview emailed to ${data.to}` : "Send attempted"); }
    catch { toast.error("Failed"); }
  };
  const waApprove = async (id) => {
    try { const { data } = await api.post(`/admin/outreach/${id}/approval-link`); window.open(data.wa_url, "_blank"); loadOutreach(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Set your WhatsApp number in Alerts first"); }
  };
  const approve = async (id) => { await api.post(`/admin/outreach/${id}/approve`); loadOutreach(); toast.success("Approved"); };
  const reject = async (id) => { await api.post(`/admin/outreach/${id}/reject`); loadOutreach(); };
  const markSent = async (id) => {
    try { await api.post(`/admin/outreach/${id}/mark-sent`); toast.success("Logged as sent"); loadOutreach(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve first"); }
  };
  const classify = async () => { if (!replyText) return; const { data } = await api.post("/admin/outreach/classify", { text: replyText }); setClassifyRes(data); };
  const saveWa = async () => { await api.post("/admin/settings", { founder_whatsapp: settings.founder_whatsapp }); toast.success("WhatsApp number saved"); };
  const sendReport = async () => {
    try { const { data } = await api.post("/admin/alerts/send-daily-report"); toast.success(data.email_sent ? `Report emailed to ${data.to}` : "Attempted"); }
    catch { toast.error("Failed"); }
  };

  if (!dash) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-40 bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center"><span className="text-white font-heading font-bold">A</span></div>
            <span className="font-heading font-bold">Make My Business AI Ready <span className="text-indigo-600">(AgenticReady)</span> <span className="text-slate-500 font-normal text-sm">Founder Console</span></span>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="text-slate-300 hover:text-white" data-testid="admin-logout"><LogOut className="h-4 w-4 mr-1" /> Log out</Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-indigo-400">Revenue First</p>
            <h1 className="font-heading text-2xl font-bold">Good day, Lanre.</h1>
          </div>
          <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">Automation {dash.automation_rate}%</Badge>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <Metric icon={DollarSign} label="Revenue Today" value={fmt(dash.revenue_today)} accent="text-emerald-400" testid="metric-revenue-today" />
          <Metric icon={DollarSign} label="Revenue Total" value={fmt(dash.revenue_total)} />
          <Metric icon={TrendingUp} label="MRR" value={fmt(dash.mrr)} accent="text-indigo-400" testid="metric-mrr" />
          <Metric icon={TrendingUp} label="MRR Added Today" value={fmt(dash.mrr_added_today)} />
          <Metric icon={Activity} label="Pipeline Value" value={fmt(dash.pipeline_value)} />
          <Metric icon={Users} label="Scans Today" value={dash.scans_today} />
          <Metric icon={Zap} label="Buyers Today" value={dash.buyers_today} />
          <Metric icon={Activity} label="Checkout Conv." value={`${dash.checkout_conversion}%`} />
        </div>

        <Tabs defaultValue="sales" className="mt-8">
          <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
            {["sales", "revenue", "opportunities", "leads", "agents", "outreach", "alerts", "report"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="capitalize data-[state=active]:bg-indigo-600 data-[state=active]:text-white">{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="sales" className="mt-5"><AdminSales /></TabsContent>

          <TabsContent value="revenue" className="mt-5 grid lg:grid-cols-2 gap-6">
            <Panel title="MRR toward target">
              <div className="flex items-end justify-between mb-2"><span className="font-heading text-3xl font-bold text-white">{fmt(dash.mrr)}</span><span className="text-slate-400 text-sm">/ {fmt(dash.revenue_target_mrr)} target</span></div>
              <div className="h-3 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500" style={{ width: `${Math.min(100, (dash.mrr / dash.revenue_target_mrr) * 100)}%` }} /></div>
              <p className="text-xs text-slate-500 mt-3">Business target: 60 customers → $50,980 MRR. Not a guarantee.</p>
            </Panel>
            <Panel title="Pipeline by plan">
              {Object.entries(dash.pipeline_breakdown).map(([k, v]) => (
                <div key={k} className="flex justify-between py-2 border-b border-slate-800 last:border-0"><span className="text-slate-300">{PLAN_LABEL[k]}</span><span className="font-mono text-indigo-400">{v} opps</span></div>
              ))}
            </Panel>
          </TabsContent>

          <TabsContent value="opportunities" className="mt-5">
            <Panel title="Top opportunities">
              <div className="divide-y divide-slate-800">
                {(dash.top_opportunities || []).map((o) => (
                  <div key={o.id} className="flex items-center justify-between py-3" data-testid="opp-row">
                    <div><p className="font-medium text-white">{o.company_name}</p><p className="text-xs text-slate-400">{o.domain} · {o.industry}</p></div>
                    <div className="text-right"><Badge className="bg-indigo-500/10 text-indigo-300 border border-indigo-500/30">{PLAN_LABEL[o.recommended_plan]}</Badge><p className="text-xs text-slate-400 mt-1">Score {o.opportunity_score} · {fmt(o.estimated_setup_value)} + {fmt(o.estimated_mrr)}/mo</p></div>
                  </div>
                ))}
              </div>
            </Panel>
          </TabsContent>

          <TabsContent value="leads" className="mt-5">
            <Panel title="Recent leads (CRM)">
              <div className="divide-y divide-slate-800">
                {(dash.recent_leads || []).map((l) => (
                  <div key={l.id} className="flex items-center justify-between py-3" data-testid="lead-row">
                    <div><p className="font-medium text-white">{l.company_name}</p><p className="text-xs text-slate-400">{l.domain} · {l.industry || "—"} {l.is_demo && <span className="text-amber-400">· DEMO</span>}</p></div>
                    <div className="text-right text-xs"><Badge variant="outline" className="border-slate-700 text-slate-300 capitalize">{l.outreach_status}</Badge>{l.opportunity_score && <p className="text-slate-400 mt-1">Opp {l.opportunity_score}</p>}</div>
                  </div>
                ))}
              </div>
            </Panel>
          </TabsContent>

          <TabsContent value="agents" className="mt-5">
            {agents && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                  <Metric icon={Bot} label="Agents Active" value={agents.summary.active} accent="text-emerald-400" />
                  <Metric icon={Activity} label="Tasks Running" value={agents.summary.tasks_running} />
                  <Metric icon={Zap} label="Approval Required" value={agents.summary.approval_required} accent="text-amber-400" />
                  <Metric icon={TrendingUp} label="Avg Confidence" value={`${agents.summary.avg_confidence}%`} />
                </div>
                <Panel title="Hyperagent swarm">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {agents.agents.map((a) => (
                      <div key={a.id} className="rounded-lg border border-slate-800 bg-slate-950 p-3" data-testid="agent-card">
                        <div className="flex items-center justify-between"><p className="font-medium text-sm text-white">{a.name}</p><span className="h-2 w-2 rounded-full bg-emerald-400 pulse-dot" /></div>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{a.mission}</p>
                        <p className="text-[10px] text-slate-600 mt-2 font-mono uppercase">{a.role} · {a.autonomy_level}</p>
                      </div>
                    ))}
                  </div>
                </Panel>
              </>
            )}
          </TabsContent>

          <TabsContent value="outreach" className="mt-5 space-y-6">
            <Panel title="Revenue Engine — discover prospects (AI-suggested research profiles)">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input data-testid="discover-industry" placeholder="Industry (e.g. boutique hotels, Shopify fashion)" value={disc.industry} onChange={(e) => setDisc({ ...disc, industry: e.target.value })} className="bg-slate-950 border-slate-700 text-white" />
                <Input type="number" min="1" max="8" value={disc.count} onChange={(e) => setDisc({ ...disc, count: Number(e.target.value) })} className="w-24 bg-slate-950 border-slate-700 text-white" />
                <Button onClick={discover} data-testid="discover-btn" className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap">Discover</Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">AI-suggested targets for research (role-based contacts, not verified). Cold email is generated & queued only — never auto-sent from the managed domain.</p>
              <div className="mt-4 divide-y divide-slate-800">
                {leads.filter((l) => l.source === "ai_discovered").slice(0, 10).map((l) => (
                  <div key={l.id} className="flex items-center justify-between py-3" data-testid="prospect-row">
                    <div><p className="font-medium text-white">{l.company_name}</p><p className="text-xs text-slate-400">{l.domain} · {l.industry} · opp {l.opportunity_score} · {l.outreach_status}</p></div>
                    <Button size="sm" variant="outline" onClick={() => genSeq(l.id)} data-testid="gen-seq-btn" className="border-slate-700 text-slate-200">Generate sequence</Button>
                  </div>
                ))}
                {leads.filter((l) => l.source === "ai_discovered").length === 0 && <p className="text-sm text-slate-500 py-3">No discovered prospects yet — run a discovery above.</p>}
              </div>
            </Panel>

            <Panel title="Outbound Center — queue & approvals">
              <div className="space-y-2">
                {(outreach.messages || []).map((m) => {
                  const c = { queued: "text-slate-300 border-slate-700", approval_requested: "text-amber-300 border-amber-500/40", approved: "text-emerald-300 border-emerald-500/40", rejected: "text-rose-300 border-rose-500/40", sent: "text-indigo-300 border-indigo-500/40" }[m.status] || "text-slate-300 border-slate-700";
                  return (
                    <div key={m.id} className="rounded-lg border border-slate-800 bg-slate-950 p-3" data-testid="outreach-msg">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0"><p className="text-sm font-medium text-white truncate">T{m.touch} · {m.name} — {m.subject}</p><p className="text-xs text-slate-400">{m.company_name} · {m.domain}</p></div>
                        <Badge variant="outline" className={`capitalize text-xs ${c}`}>{(m.status || "").replace(/_/g, " ")}</Badge>
                      </div>
                      <p className="text-xs text-slate-400 mt-2 line-clamp-2">{m.body}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Button size="sm" variant="ghost" onClick={() => testSend(m.id)} data-testid="testsend-btn" className="text-slate-300 h-7">Test-send to me</Button>
                        <Button size="sm" variant="ghost" onClick={() => waApprove(m.id)} data-testid="wa-btn" className="text-emerald-300 h-7">WhatsApp approve</Button>
                        <Button size="sm" variant="ghost" onClick={() => approve(m.id)} className="text-emerald-300 h-7">Approve</Button>
                        <Button size="sm" variant="ghost" onClick={() => reject(m.id)} className="text-rose-300 h-7">Reject</Button>
                        <Button size="sm" variant="ghost" onClick={() => markSent(m.id)} className="text-indigo-300 h-7">Mark sent</Button>
                      </div>
                    </div>
                  );
                })}
                {(outreach.messages || []).length === 0 && <p className="text-sm text-slate-500">No sequences yet.</p>}
              </div>
            </Panel>

            <Panel title="Reply classifier">
              <Textarea data-testid="reply-input" placeholder="Paste an inbound reply to classify & route…" value={replyText} onChange={(e) => setReplyText(e.target.value)} className="bg-slate-950 border-slate-700 text-white" rows={3} />
              <Button onClick={classify} data-testid="classify-btn" className="mt-3 bg-indigo-600 hover:bg-indigo-700">Classify reply</Button>
              {classifyRes && (
                <div className="mt-4 rounded-lg border border-slate-800 bg-slate-950 p-4 text-sm space-y-1">
                  <p className="text-white">Classification: <span className="text-indigo-300 font-mono">{classifyRes.classification}</span> · sentiment {classifyRes.sentiment}</p>
                  <p className="text-slate-400">Route: {classifyRes.recommended_route}</p>
                  <p className="text-slate-400">Summary: {classifyRes.summary}</p>
                  {classifyRes.suggested_reply && <p className="text-slate-300 mt-2 border-t border-slate-800 pt-2">Suggested: {classifyRes.suggested_reply}</p>}
                </div>
              )}
            </Panel>
          </TabsContent>

          <TabsContent value="alerts" className="mt-5 grid lg:grid-cols-2 gap-6">
            <Panel title="Founder alerts">
              <p className="text-sm text-slate-300">Daily report + sale alerts email to <span className="font-mono text-indigo-300">{alerts?.founder_email}</span> via Resend.</p>
              <Button onClick={sendReport} data-testid="send-report-btn" className="mt-4 bg-indigo-600 hover:bg-indigo-700">Send daily report now</Button>
              <div className="mt-5"><p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Real-time alert types</p>
                <div className="flex flex-wrap gap-2">{(alerts?.alert_types || []).map((a) => <span key={a} className="text-xs px-2 py-1 rounded-full bg-slate-800 text-slate-300">{a}</span>)}</div></div>
              <p className="text-[11px] text-slate-500 mt-4">Schedule: {alerts?.schedule?.daily_email} daily · morning brief {alerts?.schedule?.morning_brief} ({alerts?.schedule?.timezone}).</p>
            </Panel>
            <Panel title="SMS + WhatsApp (preview)">
              <pre className="text-xs font-mono text-emerald-300 whitespace-pre-wrap bg-slate-950 rounded-lg p-4 border border-slate-800" data-testid="alerts-sms-preview">{alerts?.sms_preview}</pre>
              <p className="text-[11px] text-slate-500 mt-2">SMS preview-only until Twilio keys are added.</p>
              <div className="mt-4"><p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Your WhatsApp number (for outbound approvals)</p>
                <div className="flex gap-2"><Input data-testid="wa-number-input" placeholder="+1 555 010 1234" value={settings.founder_whatsapp || ""} onChange={(e) => setSettings({ ...settings, founder_whatsapp: e.target.value })} className="bg-slate-950 border-slate-700 text-white" />
                <Button onClick={saveWa} data-testid="save-wa-btn" className="bg-slate-700 hover:bg-slate-600">Save</Button></div>
                <p className="text-[11px] text-slate-500 mt-2">Approvals open WhatsApp to this number now; auto-send activates when Twilio WhatsApp keys are added.</p></div>
            </Panel>
          </TabsContent>

          <TabsContent value="report" className="mt-5 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Panel title={report?.title || "Daily Revenue Report"}>
                {report && (
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                    {Object.entries(report.today).map(([k, v]) => (
                      <div key={k} className="flex justify-between py-1.5 border-b border-slate-800"><span className="text-slate-400 capitalize">{k.replace(/_/g, " ")}</span><span className="font-mono text-white">{typeof v === "number" && k.includes("revenue") || k.includes("mrr") ? fmt(v) : v}</span></div>
                    ))}
                  </div>
                )}
              </Panel>
            </div>
            <Panel title={<span className="flex items-center gap-2"><Smartphone className="h-4 w-4" /> Daily SMS preview</span>}>
              <pre className="text-xs font-mono text-emerald-300 whitespace-pre-wrap bg-slate-950 rounded-lg p-4 border border-slate-800" data-testid="sms-preview">{report?.sms_preview}</pre>
              <p className="text-[11px] text-slate-500 mt-3">Sent via configurable SMS provider (Twilio). Founder number is never hard-coded.</p>
            </Panel>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent = "text-white", testid }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400 text-xs"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <p className={`font-heading text-2xl font-bold mt-1.5 ${accent}`}>{value}</p>
    </div>
  );
}
function Panel({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
      <h3 className="font-heading font-semibold text-white mb-4">{title}</h3>
      {children}
    </div>
  );
}
